/* eslint-disable max-len */
import React, { useRef, useCallback, useState, useEffect, useContext } from 'react';
import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import Cropper, { ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import { IoCrop } from 'react-icons/io5';
import { GrPowerReset } from 'react-icons/gr';
import { MdRotate90DegreesCcw } from 'react-icons/md';
import { LanguageContext } from '../../../../../../../libs/Context';
import { getBlobUrl } from '../../../../../../../libs/HelperContent';

type CropArea = {
  width: number;
  height: number;
  x: number;
  y: number;
};

const EditorImage = (props) =>
{
	const { imageStyle, applyImgFilters, setApplyImgFilters, updateUploadFiles, initialState } = props;

	const cropperRef = useRef<ReactCropperElement>(null);
	const [ cropArea, setCropArea ] = useState<CropArea | null>(null);
	const { language } = useContext(LanguageContext);
	const [ cropImg, setCropImg ] = useState<boolean>(false);
	const [ rotation, setRotation ] = useState<number>(0);

	const onCrop = useCallback(() =>
	{
		const cropper = cropperRef.current?.cropper;

		if (cropper)
		{
			const cropData = cropper.getData();

			setCropArea({
				width  : cropData.width,
				height : cropData.height,
				x      : cropData.x,
				y      : cropData.y
			});
		}
	}, []);

	// roate imagefunction
	const handleRotate = async () =>
	{
		const cropper = cropperRef.current?.cropper;

		if (cropper && cropImg)
		{
			cropper.rotate(90);
		}
		else
		{
			// Handle rotation without cropper
			const newRotation = (rotation + 90) % 360;

			setRotation(newRotation);

			const img = new Image();

			img.src = applyImgFilters?.dataUrl;

			img.onload = () =>
			{
				const canvas = document.createElement('canvas');
				const ctx:any = canvas.getContext('2d');

				if (newRotation === 90 || newRotation === 270)
				{
					canvas.width = img.height;
					canvas.height = img.width;
				}
				else
				{
					canvas.width = img.width;
					canvas.height = img.height;
				}

				ctx.translate(canvas.width / 2, canvas.height / 2);
				ctx.rotate((newRotation * Math.PI) / 180);
				ctx.drawImage(img, -img.width / 2, -img.height / 2);

				canvas.toBlob((blob) =>
				{
					if (blob)
					{
						const blobUrl = URL.createObjectURL(blob);
						const newMedia = { ...applyImgFilters, dataUrl: blobUrl };

						setApplyImgFilters((prev) => ({ ...prev, ...newMedia }));
						updateUploadFiles(newMedia);
					}
				}, 'image/png');
			};
		}
	};

	// Filter action type
	const handleActionType = async (type) =>
	{
		const getdataUrl = applyImgFilters?.existImg ? applyImgFilters?.existImg : await getBlobUrl(applyImgFilters?.key);
		const newMedia = { ...applyImgFilters, dataUrl: getdataUrl, styles: initialState };

		if (type === 'reset')
		{
			setApplyImgFilters({ ...newMedia });
			setCropImg(false);
			setRotation(0);
		}
		else if (type === 'HD')
		{
			setApplyImgFilters((prev) => ({ ...prev, styles: { ...prev?.styles, HD: !prev?.styles?.HD } }));
		}
		else if (type === 'rotate')
		{
			handleRotate();
		}
		else
		{
			setCropImg(!cropImg);
		}
	};

	// Confirm crop image
	const handleConfirmCrop = () =>
	{
		const cropper = cropperRef.current?.cropper;

		if (!cropper) return;

		const croppedCanvas = cropper?.getCroppedCanvas();

		croppedCanvas.toBlob((blob) =>
		{
			if (blob)
			{
				const blobUrl = URL.createObjectURL(blob);
				const newMedia = { ...applyImgFilters, dataUrl: blobUrl };

				setApplyImgFilters((prev) => ({ ...prev, ...newMedia }));
				setCropImg(!cropImg);
				updateUploadFiles(newMedia);
			}
		}, 'image/png');
	};

	useEffect(() =>
	{
		setCropImg(false);
		setRotation(0);
	}, [ applyImgFilters?.url ]);

	return (
		<Grid
			item
			xs={12}
			md={7}
			sx={{
				display        : 'flex',
				justifyContent : 'space-between',
				flexDirection  : 'column',
				alignItems     : 'start',
				height         : 'var(--percent100)',
				gap            : 1
			}}
		>
			<Typography fontWeight='var(--font500)'>{applyImgFilters?.name}</Typography>
			{applyImgFilters?.url && (
				<Box
					sx={{
						position       : 'relative',
						aspectRatio    : '16 / 9',
						background     : '#000000',
						borderRadius   : '13px',
						overflow       : 'hidden',
						display        : 'flex',
						justifyContent : 'center',
						alignItems     : 'center',
						width          : 'var(--percent100)',
						height         : 'var(--percent100)'
					}}
				>
					{cropImg
						? (
							<>
								<Cropper
									src={applyImgFilters?.dataUrl}
									style={{
										...imageStyle,
										objectFit : 'contain',
										width     : 'var(--percent100)',
										height    : 'var(--percent100)'
									}}
									ref={cropperRef}
									viewMode={1}
									guides={false}
									background={false}
									autoCropArea={1}
									crossOrigin='anonymous'
									crop={onCrop}
								/>
								<IconButton
									onClick={handleConfirmCrop}
									sx={{
										position   : 'absolute',
										top        : 5,
										right      : 5,
										color      : 'white',
										background : 'black'
									}}
								>
									<CheckIcon />
								</IconButton>
								<IconButton
									onClick={() => handleActionType('')}
									sx={{
										position   : 'absolute',
										top        : 5,
										left       : 5,
										color      : 'white',
										background : 'black'
									}}
								>
									<CancelIcon />
								</IconButton>
							</>
						)
						: (
							<Box
								id='editableImage'
								component='img'
								src={applyImgFilters?.dataUrl}
								alt='Selected'
								style={{
									...imageStyle,
									objectFit : 'contain',
									width     : 'var(--percent100)',
									height    : 'var(--percent100)'
								}}
							/>
						)}
				</Box>
			)}
			<Box
				sx={{
					display        : 'flex',
					justifyContent : 'space-between',
					width          : 'var(--percent100)',
					alignItems     : 'center',
					height         : 'var(--percent100)'
				}}
			>
				<Button
					sx={{
						backgroundColor : 'rgba(217, 217, 217, 0.2)',
						color           : applyImgFilters?.styles?.HD ? 'var(--primaryColor)' : 'var(--black)',
						fontSize        : 'var(--pixel12)'
					}}
					onClick={() => handleActionType('HD')}
				>
					{language?.hd}
				</Button>
				<Button
					sx={{
						backgroundColor : 'rgba(217, 217, 217, 0.2)',
						color           : cropImg ? 'var(--primaryColor)' : 'var(--black)',
						fontSize        : 'var(--pixel12)'
					}}
					startIcon={<IoCrop />}
					onClick={() => handleActionType('cropedImg')}
				>
					{language?.crop}
				</Button>
				<Button
					sx={{
						backgroundColor : 'rgba(217, 217, 217, 0.2)',
						color           : rotation !== 0 ? 'var(--primaryColor)' : 'var(--black)',
						fontSize        : 'var(--pixel12)'
					}}
					startIcon={<MdRotate90DegreesCcw />}
					onClick={() => handleActionType('rotate')}
				>
					{language?.rotate || 'Rotate'}
				</Button>
				<Button
					sx={{
						backgroundColor : 'rgba(217, 217, 217, 0.2)',
						color           : applyImgFilters?.actionType === 'reset' ? 'var(--primaryColor)' : 'var(--black)',
						fontSize        : 'var(--pixel12)'
					}}
					startIcon={<GrPowerReset />}
					onClick={() => handleActionType('reset')}
				>
					{language?.original}
				</Button>
			</Box>
		</Grid>
	);
};

export default EditorImage;